import { useMemo, useRef } from "react"
import type { PlanId } from "@/lib/branchTypes"
import {
  getTrackKeyframesForPlan,
  getZoomOverridesAtDate,
  hasZoomKeyframes,
  sanitizeZoomKeyframesBySurface,
  type ZoomKeyframesByTrack,
  type ZoomOverridesAtDate,
} from "../../../extrapolated-graph/zoomKeyframes"
import savedZoomKeyframesJson from "../../../extrapolated-graph/zoomKeyframes.json"
import { easeInOutCubic } from "../../../extrapolated-graph/hooks"
import {
  HUD87_GRAPH_MINIMUM_DATE,
  HUD89_POST_CHOICE_VIEWPORT,
} from "../../../hud/variants"
import { lerpNumber } from "../interpolate"
import { timestampFromYearFraction, yearFractionOf } from "../storyTime"
import type { StripPlotArea } from "../stripLayout"
import {
  FLOP_END_YEAR_FRACTION,
  MOBILE_STRIP_CENTERED_SPAN_YEARS,
  MOBILE_STRIP_CHOICE_BEAT_YEAR_FRACTION,
  MOBILE_STRIP_FRAME_REVEAL_YEAR_FRACTION,
  clampedFrameYearFraction,
  planFrameModeFor,
  type PlanFrameMode,
} from "./planFrameModes"
import { useChoicePointBlend, useTweenedZoomChannel } from "./cameraTweens"

// The strip's camera: which time window and y-max are on screen at a
// given story date. Three layers, applied in order:
//   1. the automatic per-plan-mode frame (automaticCameraFrame),
//   2. authored mobile keyframes from /zoom-editor (override + tween),
//   3. the choice-point fan frame, blended in while the block is up.

// ---- Desktop-mirror pre-choice frame --------------------------------------
// Began as a mirror of the desktop HUD's pre-choice close-up graph; that
// desktop machinery has since been removed, so this is now the only
// implementation. Still-shared sources of truth: hud/variants.tsx
// (HUD87_*/HUD89_*) and mobileStripBandSettings.
const DESKTOP_VIEW_START_DATE = new Date("2026-01-01T00:00:00Z")
// The frozen pre-flop window runs well past the choice beat so the
// projected trajectory has runway to shoot up into the red and exit the
// strip's top edge (the purple-line beat).
const MOBILE_STRIP_FROZEN_VIEW_END_DATE = new Date("2030-07-01T00:00:00Z")
// The pre-choice y-axis is LINEAR, 1.1 → 3.
export const DESKTOP_VIEW_Y_MIN = 1.1
const DESKTOP_VIEW_Y_MAX = 3

// ---- Choice-point fan frame ------------------------------------------------
// The fan frame's window (start/end/yMax) is AUTHORED as the shared
// mobile keyframe at this date (edit on /zoom-editor). It is applied
// ONLY through the scroll-driven fan blend and stripped from the live
// pre-choice camera track (useAuthoredZoomOverrides) — left in, it would
// reframe the ordinary pre-choice close-up across [2028-06, 2029-01)
// with the choice block off screen. The axis-type switch (linear→log)
// can't be a keyframe, so it lives in stripYScale; the numeric window IS
// the keyframe.
const CHOICE_POINT_FRAME_KEYFRAME_DATE_ISO = "2028-06-01"
const CHOICE_POINT_FRAME_KEYFRAME_DATE = new Date(
  `${CHOICE_POINT_FRAME_KEYFRAME_DATE_ISO}T00:00:00Z`,
)

// Fallbacks if the fan-frame keyframe is ever cleared, so the fan can't
// collapse to a degenerate window.
const CHOICE_POINT_VIEW_START_FALLBACK_MS = Date.UTC(2028, 0, 1)
const CHOICE_POINT_VIEW_END_FALLBACK_MS = Date.UTC(2035, 6, 1) // 2035-07-01
const CHOICE_POINT_VIEW_Y_MAX_FALLBACK = 144

// The mobile surface of the two-surface keyframe file (see /zoom-editor).
const SAVED_MOBILE_ZOOM_KEYFRAMES =
  sanitizeZoomKeyframesBySurface(savedZoomKeyframesJson).mobile

export type StripCameraFrame = { yMax: number; startT: number; endT: number }

export type StripCamera = {
  // Line-reveal anchor: the current date, floored at the graph minimum.
  anchorT: number
  storyYearFraction: number
  planFrameMode: PlanFrameMode
  isViewportRevealed: boolean
  // The displayed frame, fan blend included.
  frameYMax: number
  viewStartT: number
  viewEndT: number
  viewSpan: number
  // The authored/automatic y-max BEFORE the fan blend (the linear axis
  // endpoint must hold still while the blend runs).
  framedYMax: number
  // 0 = pre-choice frame, 1 = the fan frame.
  choiceBlend: number
  choicePointFrame: { startT: number; endT: number; yMax: number }
  xForTime: (time: number) => number
  timeForX: (x: number) => number
}

type StripCameraInputs = {
  isDesktopMirror: boolean
  currentDate: Date
  currentT: number
  firstT: number
  activePlan: PlanId
  isChoicePointOnScreen: boolean
  dateIsHoverAutoplay: boolean
  zoomKeyframesOverride?: ZoomKeyframesByTrack
  plot: StripPlotArea
}

export const useStripCamera = (inputs: StripCameraInputs): StripCamera => {
  const { isDesktopMirror, currentT, firstT, plot } = inputs
  const storyYearFraction = yearFractionOf(inputs.currentDate)
  const planFrameMode = planFrameModeFor(inputs.activePlan)
  // freeze/flop-freeze run the FRAME on a clamped story clock; the band
  // anchor keys on the real one, so the red still goes live at 2029.5
  // and rides the line off even while the frame holds still.
  const frameYearFraction = clampedFrameYearFraction(planFrameMode, storyYearFraction)
  const anchorT = isDesktopMirror
    ? Math.max(currentT, HUD87_GRAPH_MINIMUM_DATE.getTime())
    : currentT
  // Teaser autoplays hold the frozen frame — the reveal belongs to the
  // real reading beat, not the hover sweep.
  const isViewportRevealed = isDesktopMirror
    && frameYearFraction >= MOBILE_STRIP_FRAME_REVEAL_YEAR_FRACTION
    && !inputs.dateIsHoverAutoplay

  const auto = automaticCameraFrame({
    isDesktopMirror,
    planFrameMode,
    frameYearFraction,
    glideProgress: centerGlideProgress(
      planFrameMode,
      storyYearFraction,
      inputs.dateIsHoverAutoplay,
    ),
    isViewportRevealed,
    anchorT,
    firstT,
    currentT,
  })
  const zoomOverrides = useAuthoredZoomOverrides(inputs)
  const choicePointFrame = useChoicePointFrame(inputs.zoomKeyframesOverride)
  // Authored keyframes override the automatic camera per channel; steps
  // between keyframe states ease via cameraTweens.
  const framed = {
    yMax: useTweenedZoomChannel(zoomOverrides.yMax, auto.yMax) ?? auto.yMax,
    startT: useTweenedZoomChannel(zoomOverrides.startTimestamp, auto.startT) ?? auto.startT,
    endT: useTweenedZoomChannel(zoomOverrides.endTimestamp, auto.endT) ?? auto.endT,
  }
  // At the choose-your-path beat the frame eases into the fan window on
  // the fan's own fade clock rather than snapping. The window scalars
  // lerp in timestamp space (xForTime rides them, so the main line,
  // band, and fan lines all slide over together); the y-axis blends in
  // screen space instead — see stripYScale.
  const choiceBlend = useChoicePointBlend(
    isDesktopMirror && inputs.isChoicePointOnScreen,
  )
  const frameYMax = lerpNumber(framed.yMax, choicePointFrame.yMax, choiceBlend)
  const viewStartT = lerpNumber(framed.startT, choicePointFrame.startT, choiceBlend)
  const viewEndT = lerpNumber(framed.endT, choicePointFrame.endT, choiceBlend)
  const viewSpan = viewEndT - viewStartT || 1

  return {
    anchorT,
    storyYearFraction,
    planFrameMode,
    isViewportRevealed,
    frameYMax,
    viewStartT,
    viewEndT,
    viewSpan,
    framedYMax: framed.yMax,
    choiceBlend,
    choicePointFrame,
    xForTime: time =>
      plot.padding.left + ((time - viewStartT) / viewSpan) * plot.innerWidth,
    timeForX: x =>
      viewStartT + ((x - plot.padding.left) / plot.innerWidth) * viewSpan,
  }
}

// ---- The automatic per-plan-mode camera ------------------------------------

type AutoFrameInputs = {
  isDesktopMirror: boolean
  planFrameMode: PlanFrameMode
  frameYearFraction: number
  glideProgress: number
  isViewportRevealed: boolean
  anchorT: number
  firstT: number
  currentT: number
}

// The date-driven frame, before keyframes and the fan blend.
const automaticCameraFrame = (inputs: AutoFrameInputs): StripCameraFrame => {
  if (!inputs.isDesktopMirror) {
    // squish: revealed history only, current date at the right edge.
    return { yMax: 0, startT: inputs.firstT, endT: inputs.currentT }
  }
  if (inputs.planFrameMode === "center-glide") return glidingCameraFrame(inputs)
  if (isCenteredRide(inputs)) {
    return {
      yMax: postChoiceYMaxAt(inputs.frameYearFraction),
      ...centeredWindowAround(inputs.anchorT),
    }
  }
  return preChoiceOrSteppedFrame(inputs)
}

// flop-center's post-flop ride: the current date stays dead center.
const isCenteredRide = (inputs: AutoFrameInputs): boolean =>
  inputs.planFrameMode === "flop-center"
  && inputs.isViewportRevealed
  && inputs.frameYearFraction >= FLOP_END_YEAR_FRACTION

const centeredWindowAround = (anchorT: number) => {
  const center = yearFractionOf(new Date(anchorT))
  return {
    startT: timestampFromYearFraction(center - MOBILE_STRIP_CENTERED_SPAN_YEARS / 2),
    endT: timestampFromYearFraction(center + MOBILE_STRIP_CENTERED_SPAN_YEARS / 2),
  }
}

// The frozen pre-choice close-up. Once revealed the y-max follows the
// flop ladder while the x-window stays FROZEN through the flop ("just a
// flop"); from the flop's end the right edge steps once a story year to
// current year + futureWindowYears, keeping the whole scenario in frame.
const preChoiceOrSteppedFrame = (inputs: AutoFrameInputs): StripCameraFrame => ({
  yMax: inputs.isViewportRevealed
    ? postChoiceYMaxAt(inputs.frameYearFraction)
    : DESKTOP_VIEW_Y_MAX,
  startT: DESKTOP_VIEW_START_DATE.getTime(),
  endT: inputs.isViewportRevealed
    && inputs.frameYearFraction >= FLOP_END_YEAR_FRACTION
    ? timestampFromYearFraction(
        Math.floor(inputs.frameYearFraction)
          + HUD89_POST_CHOICE_VIEWPORT.futureWindowYears,
      )
    : MOBILE_STRIP_FROZEN_VIEW_END_DATE.getTime(),
})

// center-glide (plan A): ONE eased move from the pre-choice close-up to
// the centered ride, then the view just rides along centered to the end.
const glidingCameraFrame = (inputs: AutoFrameInputs): StripCameraFrame => {
  const centered = centeredWindowAround(inputs.anchorT)
  const targetYMax = postChoiceYMaxAt(
    Math.max(inputs.frameYearFraction, MOBILE_STRIP_FRAME_REVEAL_YEAR_FRACTION),
  )
  return {
    yMax: lerpNumber(DESKTOP_VIEW_Y_MAX, targetYMax, inputs.glideProgress),
    startT: lerpNumber(
      DESKTOP_VIEW_START_DATE.getTime(),
      centered.startT,
      inputs.glideProgress,
    ),
    endT: lerpNumber(
      MOBILE_STRIP_FROZEN_VIEW_END_DATE.getTime(),
      centered.endT,
      inputs.glideProgress,
    ),
  }
}

// 0→1 across the choice→reveal date sweep (2029 → 2029.5), so the whole
// glide plays inside that one marker transition; teasers stay frozen.
const centerGlideProgress = (
  mode: PlanFrameMode,
  storyYearFraction: number,
  dateIsHoverAutoplay: boolean,
): number => {
  if (mode !== "center-glide" || dateIsHoverAutoplay) return 0
  const raw = (storyYearFraction - MOBILE_STRIP_CHOICE_BEAT_YEAR_FRACTION)
    / (MOBILE_STRIP_FRAME_REVEAL_YEAR_FRACTION
      - MOBILE_STRIP_CHOICE_BEAT_YEAR_FRACTION)
  return easeInOutCubic(Math.min(1, Math.max(0, raw)))
}

// The flop ladder: yMaxByYear, linearly interpolated between year
// anchors and clamped outside their range.
const postChoiceYMaxAt = (yearFraction: number): number => {
  const byYear = HUD89_POST_CHOICE_VIEWPORT.yMaxByYear
  const anchorYears = Object.keys(byYear).map(Number).sort((a, b) => a - b)
  if (yearFraction <= anchorYears[0]) return byYear[anchorYears[0]]
  const lastYear = anchorYears[anchorYears.length - 1]
  if (yearFraction >= lastYear) return byYear[lastYear]
  return ladderValueBetween(byYear, anchorYears, yearFraction)
}

const ladderValueBetween = (
  byYear: Record<number, number>,
  anchorYears: number[],
  yearFraction: number,
): number => {
  for (let i = 0; i < anchorYears.length - 1; i++) {
    const a = anchorYears[i]
    const b = anchorYears[i + 1]
    if (yearFraction < a || yearFraction >= b) continue
    return byYear[a] + (byYear[b] - byYear[a]) * ((yearFraction - a) / (b - a))
  }
  return byYear[anchorYears[anchorYears.length - 1]]
}

// ---- Authored keyframe overrides -------------------------------------------

const NO_ZOOM_OVERRIDES: ZoomOverridesAtDate = {}

// Keyframed camera overrides at the reading date: from the first set
// marker onward each channel overrides the automatic camera, held until
// the next set marker. Teaser autoplays hold the camera at the last real
// reading date so the sweep can't fire keyframe moves.
const useAuthoredZoomOverrides = (inputs: StripCameraInputs): ZoomOverridesAtDate => {
  const lastReadingDateRef = useRef(inputs.currentDate)
  if (!inputs.dateIsHoverAutoplay) lastReadingDateRef.current = inputs.currentDate
  const cameraDate = inputs.dateIsHoverAutoplay
    ? lastReadingDateRef.current
    : inputs.currentDate
  // The /zoom-editor preview passes its live in-memory tracks so edits
  // render immediately, instead of the saved zoomKeyframes.json.
  const source = inputs.zoomKeyframesOverride ?? SAVED_MOBILE_ZOOM_KEYFRAMES
  const activeKeyframes = useMemo(
    () =>
      getTrackKeyframesForPlan(source, inputs.activePlan)
        // The fan-frame keyframe authors the choice-point window ONLY
        // (applied via the fan blend) — drop it from the live pre-choice
        // camera track, in the editor preview too.
        .filter(keyframe => keyframe.date !== CHOICE_POINT_FRAME_KEYFRAME_DATE_ISO),
    [source, inputs.activePlan],
  )
  return inputs.isDesktopMirror && hasZoomKeyframes(activeKeyframes)
    ? getZoomOverridesAtDate(activeKeyframes, cameraDate.getTime())
    : NO_ZOOM_OVERRIDES
}

// The fan frame's numeric window, read off the SHARED mobile track at
// the keyframe date (plan-independent — the fan is the same for every
// plan; the per-plan 2029 keyframes are the post-choice close-ups).
const useChoicePointFrame = (override?: ZoomKeyframesByTrack) => {
  const source = override ?? SAVED_MOBILE_ZOOM_KEYFRAMES
  return useMemo(() => {
    const overrides = getZoomOverridesAtDate(
      source.shared ?? [],
      CHOICE_POINT_FRAME_KEYFRAME_DATE.getTime(),
    )
    return {
      startT: overrides.startTimestamp ?? CHOICE_POINT_VIEW_START_FALLBACK_MS,
      endT: overrides.endTimestamp ?? CHOICE_POINT_VIEW_END_FALLBACK_MS,
      yMax: overrides.yMax ?? CHOICE_POINT_VIEW_Y_MAX_FALLBACK,
    }
  }, [source])
}
